import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUserInfo } from "@redux/slices/authSlice";
import { Bot } from "lucide-react";
import { toast } from "react-toastify";
import ReactFlow, {
  Background,
  MarkerType,
  Handle,
  Position,
  ReactFlowProvider,
} from "reactflow";
import "reactflow/dist/style.css";
import { getPipelineDetail } from "@services/httpservice";
import D2IntegrationDiagram from "@components/agent/D2IntegrationDiagram";
import ScrollContainer from "@components/misc/ScrollContainer";

// ── Field node components (same as PipelineView) ──────────────────────────────

const SrcFieldNode = ({ data }) => (
  <div style={{
    border: "1.5px solid #93c5fd", borderRadius: 5, background: "white",
    padding: "5px 12px", fontSize: 12, fontWeight: 600, color: "#1e40af",
    whiteSpace: "nowrap", boxShadow: "0 1px 3px rgba(0,0,0,0.06)", minWidth: 110,
  }}>
    {data.label}
    <Handle type="source" position={Position.Right}
      style={{ background: "#3b82f6", border: "2px solid white", width: 9, height: 9 }} />
  </div>
);

const DstFieldNode = ({ data }) => (
  <div style={{
    border: "1.5px solid #93c5fd", borderRadius: 5, background: "white",
    padding: "5px 12px", fontSize: 12, fontWeight: 600, color: "#1e40af",
    whiteSpace: "nowrap", boxShadow: "0 1px 3px rgba(0,0,0,0.06)", minWidth: 110,
  }}>
    <Handle type="target" position={Position.Left}
      style={{ background: "#3b82f6", border: "2px solid white", width: 9, height: 9 }} />
    {data.label}
  </div>
);

const PlatformLabelNode = ({ data }) => (
  <div style={{
    border: "2px solid #1d4ed8", borderRadius: 6,
    background: "#1d4ed8", padding: "7px 18px",
    fontSize: 13, fontWeight: 700, color: "white",
    textAlign: "center", minWidth: 110,
    boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
  }}>
    {data.label}
  </div>
);

const MAPPING_NODE_TYPES = {
  srcField: SrcFieldNode,
  dstField: DstFieldNode,
  platformLabel: PlatformLabelNode,
};

// ── Build mapping ReactFlow graph (same as PipelineView) ──────────────────────

function buildMappingGraph(mappingRows, srcPlatform, dstPlatform) {
  const nodes = [];
  const edges = [];
  const xSrc = 20, xDst = 310, yPlat = 10, yStart = 80, yStep = 52;

  nodes.push({ id: "mp-src-plat", type: "platformLabel", position: { x: xSrc, y: yPlat }, data: { label: srcPlatform || "Source" }, draggable: false, selectable: false });
  nodes.push({ id: "mp-dst-plat", type: "platformLabel", position: { x: xDst, y: yPlat }, data: { label: dstPlatform || "Destination" }, draggable: false, selectable: false });

  mappingRows.forEach((row, i) => {
    const y = yStart + i * yStep;
    nodes.push({ id: "mp-src-" + i, type: "srcField", position: { x: xSrc, y }, data: { label: row.from }, draggable: false, selectable: false });
    nodes.push({ id: "mp-dst-" + i, type: "dstField", position: { x: xDst, y }, data: { label: row.to }, draggable: false, selectable: false });
    edges.push({
      id: "mp-e-" + i, source: "mp-src-" + i, target: "mp-dst-" + i, type: "default",
      style: { stroke: "#60a5fa", strokeWidth: 1.5, strokeDasharray: "6,3" },
      markerEnd: { type: MarkerType.ArrowClosed, color: "#3b82f6", width: 14, height: 14 },
    });
  });

  return { nodes, edges };
}

const MappingCanvas = ({ mappingRows, srcPlatform, dstPlatform }) => {
  const { nodes: mapNodes, edges: mapEdges } = useMemo(
    () => buildMappingGraph(mappingRows, srcPlatform, dstPlatform),
    [mappingRows, srcPlatform, dstPlatform]
  );
  const canvasH = Math.max(280, mappingRows.length * 52 + 130);
  return (
    <ReactFlowProvider>
      <div style={{ height: canvasH, width: "100%" }}>
        <ReactFlow
          nodes={mapNodes} edges={mapEdges} nodeTypes={MAPPING_NODE_TYPES}
          fitView fitViewOptions={{ padding: 0.18 }}
          nodesDraggable={false} nodesConnectable={false}
          elementsSelectable={false} zoomOnScroll={false}
          panOnScroll={false} panOnDrag={false}
          preventScrolling={false} proOptions={{ hideAttribution: true }}
        >
          <Background variant="dots" gap={18} size={1} color="#f0f0f0" />
        </ReactFlow>
      </div>
    </ReactFlowProvider>
  );
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function legPlatformName(leg) {
  if (!leg) return "-";
  return (
    leg.destinationDetails?.destinationType ||
    leg.destinationDetails?.platform ||
    leg.sourceDetails?.platform ||
    leg.triggerDetails?.triggerType ||
    "Step"
  );
}

function parseMappingRows(mappingDetails) {
  if (!mappingDetails) return [];
  const raw = Array.isArray(mappingDetails) ? mappingDetails
    : Array.isArray(mappingDetails?.mappings) ? mappingDetails.mappings
    : Array.isArray(mappingDetails?.field_mappings) ? mappingDetails.field_mappings : [];
  return raw.map((entry) => {
    if (!entry) return null;
    // Normalized format: { source: { label, id/key }, target: { label, id/key } }
    if (entry.source && typeof entry.source === "object") {
      const from = entry.source?.label || entry.source?.id || entry.source?.key || "";
      const to   = entry.target?.label || entry.target?.id || entry.target?.key || "";
      return (from || to) ? { from, to } : null;
    }
    // Flat format with any combination of label / field / field_id keys
    const from = entry.source_label || entry.source_field || entry.source_field_id || "";
    const to   = entry.destination_label || entry.destination_field || entry.destination_field_id
               || entry.target_label || entry.target_field || entry.target_field_id || "";
    return (from || to) ? { from, to } : null;
  }).filter(Boolean);
}

// ── Right panel (step detail) ─────────────────────────────────────────────────

// ── Right panel: one connection A → B ────────────────────────────────────────
// connIndex 0 = first A→B link, 1 = B→C, etc.
// fromLeg is always the upstream leg (could be the trigger).
// toLeg   is always the downstream leg.

const ConnectionPanel = ({ fromLeg, toLeg, fromPlatform, toPlatform, connIndex, allLegs, navigate, pipelineId }) => {
  const isTriggerAsSource = connIndex === 0;

  // Mapping rows: prefer toLeg then fromLeg so both storage patterns are covered.
  const toLegRows   = parseMappingRows(toLeg?.mappingDetails);
  const fromLegRows = parseMappingRows(fromLeg?.mappingDetails);
  const mappingRows = toLegRows.length > 0 ? toLegRows : fromLegRows;

  const s3Lookup       = toLeg?.sourceDetails?.s3_lookup;
  const triggerFields  = fromLeg?.triggerDetails?.fields || [];
  const triggerEvent   = fromLeg?.triggerDetails?.triggerEvent || "";
  const fromObjectType = fromLeg?.sourceDetails?.object_type || fromLeg?.triggerDetails?.objectType || "";

  const hasS3LookupData = s3Lookup && (
    s3Lookup.file_path || s3Lookup.match_column || s3Lookup.match_from || (s3Lookup.pick_columns?.length > 0)
  );

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto">

        {/* S3 lookup config */}
        {hasS3LookupData ? (
          <div className="px-4 pt-2">
            <div className="px-3 pt-1 pb-1 flex items-center justify-between">
              <span className="text-xs font-bold text-gray-900 tracking-wide">S3 Lookup Config</span>
            </div>
            {(s3Lookup.match_from || s3Lookup.match_column) && (
              <div className="mb-3 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2">
                <div className="text-[0.68rem] font-black uppercase tracking-widest text-blue-500 mb-1.5">Match Key</div>
                <div className="flex items-center gap-2 text-xs font-semibold flex-wrap">
                  <span className="rounded border border-blue-200 bg-white px-2 py-1 text-blue-700">{s3Lookup.match_from || "—"}</span>
                  <svg className="w-3 h-3 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6"/></svg>
                  <span className="rounded border border-blue-200 bg-white px-2 py-1 text-blue-700">{s3Lookup.match_column || "—"}</span>
                </div>
              </div>
            )}
            <table className="w-full text-xs border border-gray-200 rounded-lg overflow-hidden mb-3">
              <thead><tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-3 py-2 text-left font-semibold text-gray-600">Property</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-600">Value</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {[["File path", s3Lookup.file_path],["Match column (S3)", s3Lookup.match_column],["Match from payload", s3Lookup.match_from],["Pick columns", Array.isArray(s3Lookup.pick_columns) ? s3Lookup.pick_columns.join(", ") : s3Lookup.pick_columns]].filter(([,v]) => v).map(([label, value]) => (
                  <tr key={label}><td className="px-3 py-2 text-gray-500">{label}</td><td className="px-3 py-2 font-mono text-gray-800">{value}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          /* Field mapping canvas — identical to PipelineView */
          <div className="flex-1">
            <div className="px-4 pt-3 pb-1 flex items-center justify-between">
              <span className="text-xs font-bold text-gray-900 tracking-wide">Field Mapping</span>
              {mappingRows.length > 0 && (
                <span className="text-xs font-semibold text-blue-600">
                  {mappingRows.length} field{mappingRows.length !== 1 ? "s" : ""}
                </span>
              )}
            </div>
            {mappingRows.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-900">
                <svg className="w-8 h-8 mb-2 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                </svg>
                <p className="text-sm">No field mapping configured.</p>
              </div>
            ) : (
              <MappingCanvas
                mappingRows={mappingRows}
                srcPlatform={fromPlatform}
                dstPlatform={toPlatform}
              />
            )}
          </div>
        )}
      </div>

      {/* View full integration details — uses the destination leg's integration_id */}
      <div className="px-4 py-3 border-t border-gray-100 flex-shrink-0">
        {toLeg?.integration_id && (
          <button
            onClick={() => navigate(`/integrations/${toLeg.integration_id}/details`)}
            className="text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline transition-colors"
          >
            View full integration details &rarr;
          </button>
        )}
      </div>
    </div>
  );
};
// ── Main page ─────────────────────────────────────────────────────────────────

const SequentialPipelineView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const userInfo = useSelector(selectUserInfo);

  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [leftWidth, setLeftWidth] = useState(55);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  // Draggable split handle (same as PipelineView)
  const onDragStart = useCallback((e) => {
    e.preventDefault();
    dragging.current = true;
    const rafRef = { current: null };
    const onMove = (ev) => {
      if (!dragging.current || !containerRef.current) return;
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        if (!dragging.current || !containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const pct = ((ev.clientX - rect.left) / rect.width) * 100;
        setLeftWidth(Math.min(80, Math.max(20, pct)));
      });
    };
    const onUp = () => {
      dragging.current = false;
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getPipelineDetail(id)
      .then((data) => { if (!cancelled) { setPipeline(data); setLoading(false); } })
      .catch((err) => { if (!cancelled) { setError(err?.message || "Failed to load integration."); setLoading(false); } });
    return () => { cancelled = true; };
  }, [id]);

  const legs = useMemo(() => {
    if (!pipeline) return [];
    return [...(pipeline.legs || [])].sort((a, b) => (a.leg_order ?? 0) - (b.leg_order ?? 0));
  }, [pipeline]);

  const platforms = useMemo(() => legs.map(legPlatformName), [legs]);

  // Build D2 graph for left panel
  const d2Graph = useMemo(() => {
    if (!legs.length) return null;
    const nodes = legs.map((leg, idx) => ({
      id: "seq-" + idx,
      label: platforms[idx],
      type: idx === 0 ? "trigger" : "connector",
    }));
    const edges = legs.slice(1).map((_, idx) => ({
      from: "seq-" + idx,
      to: "seq-" + (idx + 1),
    }));
    return { nodes, edges };
  }, [legs, platforms]);

  // Connections = N-1 pairs for N legs: [A→B, B→C, …]
  const connections = useMemo(
    () => legs.slice(0, -1).map((fromLeg, idx) => ({
      fromLeg,
      toLeg: legs[idx + 1],
      fromPlatform: platforms[idx],
      toPlatform: platforms[idx + 1],
      label: `${platforms[idx]} → ${platforms[idx + 1]}`,
    })),
    [legs, platforms]
  );
  const totalConnections = connections.length;
  const activeConnection = connections[activeStep] || null;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
          <p className="text-sm text-gray-900">Loading workflow...</p>
        </div>
      </div>
    );
  }

  if (error || !pipeline) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <p className="text-red-600 font-semibold">{error || "Integration not found."}</p>
        <button onClick={() => navigate("/integrations")} className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-bold text-white hover:bg-blue-700">
          Back to Integrations
        </button>
      </div>
    );
  }

  const chainTitle = pipeline.name || platforms.join(" > ");
  const pipelineUrl = (process.env.REACT_APP_API_URL || "http://127.0.0.1:8000").replace(/\/$/, "")
    + "/api/integrations/pipelines/" + id + "/execute-sequential/";

  return (
    <div className="flex flex-col bg-gray-50 full-bleed" style={{ minHeight: "calc(100vh - 64px)" }}>

      {/* Header - identical to PipelineView */}
      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4 shadow-sm flex-shrink-0">
        <div className="max-w-full flex items-center gap-4 flex-wrap">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 transition-all font-sans"
          >
            &larr; Back
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg sm:text-xl font-bold text-gray-900 truncate">{chainTitle}</h1>
          </div>
          {userInfo?.role?.toLowerCase() !== "user" && (
            <button
              onClick={() => navigate(pipeline.agent_session_id ? "/agent?session=" + pipeline.agent_session_id : "/agent")}
              className="flex items-center justify-center gap-x-2 rounded-full bg-blue-600 border border-blue-700 px-5 py-2.5 text-white text-sm font-bold transition-all hover:bg-blue-700 shadow-sm active:scale-95 flex-shrink-0 whitespace-nowrap"
            >
              <Bot className="h-5 w-5" />
              Launch Agent
            </button>
          )}
        </div>

        {/* Webhook URL bar - identical to PipelineView */}
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-2.5">
          <div className="flex items-center gap-2 flex-grow overflow-hidden">
            <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] sm:text-xs font-bold text-blue-700 uppercase tracking-tight">Webhook URL</div>
              <ScrollContainer className="mt-0.5">
                <div className="text-xs text-blue-600 font-mono whitespace-nowrap pr-4">{pipelineUrl}</div>
              </ScrollContainer>
            </div>
          </div>
          <button
            onClick={() => { navigator.clipboard.writeText(pipelineUrl); toast.info("Copied to clipboard!"); }}
            className="w-full sm:w-auto flex-shrink-0 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-blue-700 transition-colors"
          >
            Copy URL
          </button>
        </div>
      </div>

      {/* Split panel - identical structure to PipelineView */}
      <div
        ref={containerRef}
        className="flex flex-col lg:flex-row flex-1 overflow-hidden lg:overflow-visible"
        style={{ height: "calc(100vh - 170px)" }}
      >
        {/* Left: D2 workflow diagram */}
        <div
          className="bg-white relative flex flex-col flex-shrink-0 border-b lg:border-b-0 border-gray-200"
          style={{
            width: window.innerWidth >= 1024 ? leftWidth + "%" : "100%",
            height: window.innerWidth >= 1024 ? "100%" : "350px",
            overflow: "hidden",
          }}
        >
          <div className="px-3 py-3 flex items-center gap-2 flex-shrink-0 bg-gray-50 lg:bg-white border-b lg:border-b-0 border-gray-100">
            <span className="text-xs sm:text-sm font-bold text-gray-900 uppercase tracking-tight opacity-70">Workflow Overview</span>
          </div>
          <div className="flex-1 min-h-0 flex flex-col">
            {d2Graph ? (
              <div className="w-full h-full flex flex-col items-center justify-center">
                <D2IntegrationDiagram flowGraph={d2Graph} fullHeight={true} />
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                No workflow graph available yet.
              </div>
            )}
          </div>
        </div>

        {/* Drag handle - identical to PipelineView */}
        <div
          onMouseDown={onDragStart}
          className="hidden lg:flex flex-shrink-0 items-center justify-center bg-gray-100 hover:bg-blue-100 border-x border-gray-200 cursor-col-resize group"
          style={{ width: "10px" }}
          title="Drag to resize"
        >
          <div className="w-0.5 h-8 rounded-full bg-gray-300 group-hover:bg-blue-400 transition-colors" />
        </div>

        {/* Right: step mapping panel */}
        <div className="flex flex-col bg-white overflow-hidden flex-1" style={{ minWidth: 0 }}>

          {/* Panel content */}
          <div className="flex-1 overflow-y-auto">
            {activeConnection ? (
              <ConnectionPanel
                fromLeg={activeConnection.fromLeg}
                toLeg={activeConnection.toLeg}
                fromPlatform={activeConnection.fromPlatform}
                toPlatform={activeConnection.toPlatform}
                connIndex={activeStep}
                allLegs={legs}
                navigate={navigate}
                pipelineId={id}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                No mapping connections available.
              </div>
            )}
          </div>

          {/* Prev / Next */}
          {totalConnections > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50 flex-shrink-0 sticky bottom-0 z-10">
              <button
                onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
                disabled={activeStep === 0}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold text-blue-600 border border-blue-200 bg-white hover:bg-blue-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
                Prev
              </button>
              <div className="text-xs font-bold text-gray-500 bg-gray-200/50 px-3 py-1 rounded-full">
                <span className="text-blue-600">{activeStep + 1}</span> / {totalConnections}
              </div>
              <button
                onClick={() => setActiveStep((s) => Math.min(totalConnections - 1, s + 1))}
                disabled={activeStep === totalConnections - 1}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold text-blue-600 border border-blue-200 bg-white hover:bg-blue-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                Next
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default SequentialPipelineView;